import { IconProps } from '@mui/material';
import * as React from 'react';
import SimpleMenu from 'src/components/atoms/SimpleMenu';
import { AgButton, AgText } from 'src/components/ui';
import { cn } from 'src/components/ui/utils';
import useAnchorEl from 'src/hooks/useAnchorEl';
import { SidebarLinkItem } from './SidebarLink';

export type SidebarMinimizedLinkProps = {
  Icon: React.FC<IconProps>;
  links: Omit<SidebarLinkItem, 'icon'>[];
  current: boolean;
  title: string;
};

// Module-level registry ensuring only one minimized popover is open at a time.
const openCloseHandlers = new Set<() => void>();

function closeAllExcept(current: () => void) {
  openCloseHandlers.forEach((close) => {
    if (close !== current) close();
  });
}

export function SidebarMinimizedLink({ Icon, links, current, title }: SidebarMinimizedLinkProps) {
  const { ref, anchorEl, open, handleOpen, handleForceClose } = useAnchorEl();
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isHoveringMenuRef = React.useRef(false);

  // Register this instance's close handler so siblings can force-close it.
  React.useEffect(() => {
    const close = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      isHoveringMenuRef.current = false;
      handleForceClose();
    };
    openCloseHandlers.add(close);
    return () => {
      openCloseHandlers.delete(close);
    };
  }, [handleForceClose]);

  const handleButtonHoverStart = React.useCallback(() => {
    closeAllExcept(handleForceClose);
    handleOpen();
  }, [handleOpen, handleForceClose]);

  const handleButtonHoverEnd = React.useCallback(() => {
    // Delay close to allow mouse to reach the menu
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringMenuRef.current) {
        handleForceClose();
      }
    }, 100);
  }, [handleForceClose]);

  const handleMenuMouseEnter = React.useCallback(() => {
    isHoveringMenuRef.current = true;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMenuMouseLeave = React.useCallback(() => {
    isHoveringMenuRef.current = false;
    handleForceClose();
  }, [handleForceClose]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AgButton
      ref={ref}
      shape="rounded"
      variant="ghost"
      size="icon-sm"
      className={cn('relative rounded-[10px]', { 'bg-brand-1': current })}
      onHoverStart={handleButtonHoverStart}
      onHoverEnd={handleButtonHoverEnd}
      onPress={handleButtonHoverStart}
    >
      <div
        className={cn(
          'center w-5 h-5 [&_svg]:w-full [&_svg]:h-full',
          current ? 'text-brand-7' : 'text-neutral-alpha-8'
        )}
      >
        <Icon />
      </div>

      {open && (
        <SimpleMenu anchorEl={anchorEl} onMouseEnter={handleMenuMouseEnter} onMouseLeave={handleMenuMouseLeave}>
          <div className="col">
            <AgText size="13" weight="medium" className="px-4 py-2 border-b border-neutral-4">
              {title}
            </AgText>
            {links.map((link) => {
              const highlight = location.pathname === link.href;
              return (
                <AgButton
                  key={link.text}
                  isDisabled={link.disabled}
                  shape="square"
                  variant="ghost"
                  className={cn('w-full relative justify-start', highlight && 'bg-brand-1')}
                  href={link.href}
                  data-testid={link.testId}
                >
                  <AgText size="13" color={highlight ? 'brand' : 'secondary'} weight="medium">
                    {link.text}
                  </AgText>
                </AgButton>
              );
            })}
          </div>
        </SimpleMenu>
      )}
    </AgButton>
  );
}
